'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { z } from 'zod'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { useLazyQuery, useQuery, useMutation } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { DataTableManual } from '../../components/shared/data-table-manual/data-table-manual'

import { DataTableToolbar } from '../../components/shared/data-table-manual/data-table-toolbar-manual'
import { useDrawerDispatch } from '@/context/DrawerContext'
import CustomerGroupForm from '../CustomerGroups/CustomerGroupForm'
import B2BCustomerForm from './B2BCustomerForm'
import { getColumns } from './components/columns'
import { BulkEditModal } from './components/BulkEditModal'
import {
  GET_B2B_CUSTOMERS,
  GET_DELETED_B2B_CUSTOMERS,
  GET_CUSTOMERS_GROUPS,
  GET_PRICELISTS,
  DELETE_MANY_CUSTOMERS,
  GET_UNIQUE_COUNTRIES,
  BULK_UPDATE_B2B_CUSTOMERS,
  RESTORE_B2B_CUSTOMER,
} from './B2BCustomersQueries'
import { GET_TAGS_FOR_SELECT } from '../Tags/tagsQueries'
import { GET_FABRIC_PALETTES } from '../FabricPalettes/fabricPaletteQueries'
import { Button } from '@/components/ui/button'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import useImpersonateB2BCustomer from '@/hooks/useImpersonateB2BCustomer'
import { toast } from 'sonner'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'
import { useMemo } from 'react'

export default function B2BCustomersTable() {
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: { findManyCustomerGroup: customersGroups } = {}, error: er2 } =
    useQuery(GET_CUSTOMERS_GROUPS)

  const { data: { findManyPriceList: priceLists } = {}, error: erę } =
    useQuery(GET_PRICELISTS)

  const { data: { findManyTag: tags } = {} } = useQuery(GET_TAGS_FOR_SELECT)

  const { data: { getUniqueCountries: countries } = {} } =
    useQuery(GET_UNIQUE_COUNTRIES)

  const { data: { findManyFabricPalette: palettes } = {} } = useQuery(
    GET_FABRIC_PALETTES,
    {
      variables: { take: 100, searchTerm: '' },
    }
  )

  const countryOptions = useMemo(() => {
    if (!countries) return []

    return (
      Array.from(
        new Set(
          countries
            // .map((item: any) => item.country)
            .filter(Boolean)
            .map((country: string) => country.toUpperCase())
        )
      ) as string[]
    )
      .sort()
      .map((country: string) => ({
        label: country,
        value: country,
      }))
  }, [countries])

  // Initialize search term directly from URL to avoid race conditions on page refresh
  const [searchTerm, setSearchTerm] = useState(() => {
    return searchParams.get('search') || ''
  })
  const [tableData, setTableData] = useState([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [showDeleted, setShowDeleted] = useState(false)
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  )

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  })

  // Bulk edit state
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = React.useState(false)
  const [selectedCustomersForBulkEdit, setSelectedCustomersForBulkEdit] =
    React.useState<any[]>([])
  const [selectionMode, setSelectionMode] = React.useState<
    'individual' | 'all-filtered'
  >('individual')
  const [isAllFilteredSelected, setIsAllFilteredSelected] =
    React.useState(false)
  const [bulkEditProperty, setBulkEditProperty] = React.useState<
    'configurator' | 'claims' | 'palettes'
  >('configurator')

  const [loadCustomers, { data, loading }] = useLazyQuery(
    GET_B2B_CUSTOMERS,
    {
      variables: {},
      fetchPolicy: 'cache-and-network',
    }
  )

  const [loadDeletedCustomers, { data: deletedData, loading: deletedLoading }] =
    useLazyQuery(GET_DELETED_B2B_CUSTOMERS, {
      variables: {},
      fetchPolicy: 'cache-and-network',
    })

  const [restoreCustomer, { loading: restoreLoading }] = useMutation(
    RESTORE_B2B_CUSTOMER,
    {
      onError: error => {
        console.error('Error restoring customer:', error)
        toast.error('Failed to restore customer')
      },
      onCompleted: () => {
        toast.success('Customer restored successfully')
        // Refetch deleted customers list
        loadDeletedCustomers({
          variables: {
            searchTerm,
            language: 'lt',
            take: pagination.pageSize,
            skip: pagination.pageIndex * pagination.pageSize,
            orderBy: getOrderSortParams(order),
          },
        })
      },
      refetchQueries: ['GET_B2B_CUSTOMERS', 'GET_DELETED_B2B_CUSTOMERS'],
    }
  )

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_CUSTOMERS,
    refetchQueries: [
      'GET_CUSTOMER_GROUPS',
      'GET_CUSTOMER_GROUP',
      'GET_B2B_CUSTOMERS',
    ],
    messages: {
      success: 'Customer deleted successfully',
      error: 'Failed to delete customer',
    },
    itemName: 'customer',
  })

  // Bulk update mutation
  const [bulkUpdateCustomers, { loading: bulkUpdateLoading }] = useMutation(
    BULK_UPDATE_B2B_CUSTOMERS,
    {
      onError: error => {
        console.error('Error bulk updating customers:', error)
        toast.error('Failed to update customers')
      },
      onCompleted: response => {
        toast.success(
          `Successfully updated ${
            response.bulkUpdateB2BCustomers.count
          } customer${response.bulkUpdateB2BCustomers.count === 1 ? '' : 's'}`
        )
        setIsBulkEditModalOpen(false)
        setSelectedCustomersForBulkEdit([])
        // Refetch the customers data
        loadCustomers({
          variables: {
            searchTerm,
            language: 'lt',
            take: pagination.pageSize,
            skip: pagination.pageIndex * pagination.pageSize,
            filters: Array.from(filters.entries())
              .map(([key, value]) => {
                if (!value.size) return null
                if (key === 'tags.id') {
                  return {
                    tags: {
                      some: {
                        id: {
                          in: Array.from(value).map(id => Number(id)),
                        },
                      },
                    },
                  }
                }
                if (key === 'fabric_palettes.id') {
                  return {
                    fabric_palettes: {
                      some: {
                        id: {
                          in: Array.from(value).map(id => Number(id)),
                        },
                      },
                    },
                  }
                }
                if (key === 'b2b_company_country') {
                  return {
                    b2b_company_address: {
                      is: {
                        country: {
                          in: Array.from(value),
                        },
                      },
                    },
                  }
                }
                if (key === 'customer_groupId' || key === 'price_listId') {
                  return {
                    [key]: {
                      in: Array.from(value).map(id => Number(id)),
                    },
                  }
                }
                return {
                  [key]: {
                    in: Array.from(value),
                  },
                }
              })
              .filter(Boolean),
            orderBy: getOrderSortParams(order),
          },
        })
      },
      refetchQueries: ['GET_B2B_CUSTOMERS'],
    }
  )

  // Handle "Select All Filtered" button click
  const handleSelectAllFiltered = () => {
    setSelectionMode('all-filtered')
    setIsAllFilteredSelected(true)
    setSelectedCustomersForBulkEdit([]) // Clear individual selections
  }

  // Handle "Clear Selection" button click
  const handleClearSelection = () => {
    setSelectionMode('individual')
    setIsAllFilteredSelected(false)
    setSelectedCustomersForBulkEdit([])
  }

  // Handle bulk edit
  const handleBulkEdit = (selectedCustomers: any[], property: string) => {
    setBulkEditProperty(property as 'configurator' | 'claims' | 'palettes')
    if (selectionMode === 'all-filtered') {
      // For filter-based selection, we don't need the individual customers
      setSelectedCustomersForBulkEdit([])
    } else {
      // For individual selection, use the selected customers
      setSelectedCustomersForBulkEdit(selectedCustomers)
    }
    setIsBulkEditModalOpen(true)
  }

  // Handle bulk edit form submission
  const handleBulkEditSubmit = (data: any) => {
    const isConfiguratorUpdate = 'is_configurator_enabled' in data
    const isClaimsUpdate = 'is_claims_enabled' in data
    const isPalettesUpdate = 'selectedPalettes' in data

    if (selectionMode === 'all-filtered') {
      // Use filter-based update
      const queryFilter = Array.from(filters.entries())
        .map(([key, value]) => {
          if (!value.size) return null
          if (key === 'tags.id') {
            return {
              tags: {
                some: {
                  id: {
                    in: Array.from(value).map(id => Number(id)),
                  },
                },
              },
            }
          }
          if (key === 'fabric_palettes.id') {
            return {
              fabric_palettes: {
                some: {
                  id: {
                    in: Array.from(value).map(id => Number(id)),
                  },
                },
              },
            }
          }
          if (key === 'b2b_company_country') {
            return {
              b2b_company_address: {
                is: {
                  country: {
                    in: Array.from(value),
                  },
                },
              },
            }
          }
          if (key === 'customer_groupId' || key === 'price_listId') {
            return {
              [key]: {
                in: Array.from(value).map(id => Number(id)),
              },
            }
          }
          return {
            [key]: {
              in: Array.from(value),
            },
          }
        })
        .filter(Boolean)

      if (isConfiguratorUpdate) {
        bulkUpdateCustomers({
          variables: {
            filters: queryFilter,
            searchTerm,
            is_configurator_enabled: data.is_configurator_enabled,
          },
        })
      } else if (isClaimsUpdate) {
        bulkUpdateCustomers({
          variables: {
            filters: queryFilter,
            searchTerm,
            is_claims_enabled: data.is_claims_enabled,
          },
        })
      } else if (isPalettesUpdate) {
        bulkUpdateCustomers({
          variables: {
            filters: queryFilter,
            searchTerm,
            palettes: data.selectedPalettes,
            palettesAction: data.action,
          },
        })
      }
    } else {
      // Use individual customer IDs
      const customerIds = selectedCustomersForBulkEdit.map(
        customer => customer.id
      )

      if (isConfiguratorUpdate) {
        bulkUpdateCustomers({
          variables: {
            customerIds,
            is_configurator_enabled: data.is_configurator_enabled,
          },
        })
      } else if (isClaimsUpdate) {
        bulkUpdateCustomers({
          variables: {
            customerIds,
            is_claims_enabled: data.is_claims_enabled,
          },
        })
      } else if (isPalettesUpdate) {
        bulkUpdateCustomers({
          variables: {
            customerIds,
            palettes: data.selectedPalettes,
            palettesAction: data.action,
          },
        })
      }
    }
  }

  // Parse URL query parameters and set filters
  const parseFiltersFromURL = () => {
    const newFilters = new Map<string, Set<string>>()

    // Parse customer groups parameter
    const customerGroupsParam = searchParams.get('customerGroups')
    if (customerGroupsParam) {
      const groupIds = customerGroupsParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id)
      if (groupIds.length > 0) {
        newFilters.set('customer_groupId', new Set(groupIds))
      }
    }

    // Parse price lists parameter
    const priceListsParam = searchParams.get('priceLists')
    if (priceListsParam) {
      const priceListIds = priceListsParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id)
      if (priceListIds.length > 0) {
        newFilters.set('price_listId', new Set(priceListIds))
      }
    }

    // Parse tags parameter
    const tagsParam = searchParams.get('tags')
    if (tagsParam) {
      const tagIds = tagsParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id)
      if (tagIds.length > 0) {
        newFilters.set('tags.id', new Set(tagIds))
      }
    }

    // Parse countries parameter
    const countriesParam = searchParams.get('countries')
    if (countriesParam) {
      const countryValues = countriesParam
        .split(',')
        .map(country => decodeURIComponent(country.trim()))
        .filter(country => country)
      if (countryValues.length > 0) {
        newFilters.set('b2b_company_country', new Set(countryValues))
      }
    }

    // Parse palettes parameter
    const palettesParam = searchParams.get('palettes')
    if (palettesParam) {
      const paletteIds = palettesParam
        .split(',')
        .map(id => id.trim())
        .filter(id => id)
      if (paletteIds.length > 0) {
        newFilters.set('fabric_palettes.id', new Set(paletteIds))
      }
    }

    return newFilters
  }

  // Update URL with current filters
  const updateURLFromFilters = (
    currentFilters: Map<string, Set<any>>,
    currentSearchTerm: string
  ) => {
    const newSearchParams = new URLSearchParams(searchParams)

    // Handle customer groups
    const customerGroupsFilter = currentFilters.get('customer_groupId')
    if (customerGroupsFilter && customerGroupsFilter.size > 0) {
      newSearchParams.set(
        'customerGroups',
        Array.from(customerGroupsFilter).join(',')
      )
    } else {
      newSearchParams.delete('customerGroups')
    }

    // Handle price lists
    const priceListsFilter = currentFilters.get('price_listId')
    if (priceListsFilter && priceListsFilter.size > 0) {
      newSearchParams.set('priceLists', Array.from(priceListsFilter).join(','))
    } else {
      newSearchParams.delete('priceLists')
    }

    // Handle tags
    const tagsFilter = currentFilters.get('tags.id')
    if (tagsFilter && tagsFilter.size > 0) {
      newSearchParams.set('tags', Array.from(tagsFilter).join(','))
    } else {
      newSearchParams.delete('tags')
    }

    // Handle countries
    const countriesFilter = currentFilters.get('b2b_company_country')
    if (countriesFilter && countriesFilter.size > 0) {
      newSearchParams.set(
        'countries',
        Array.from(countriesFilter)
          .map(country => encodeURIComponent(country))
          .join(',')
      )
    } else {
      newSearchParams.delete('countries')
    }

    // Handle palettes
    const palettesFilter = currentFilters.get('fabric_palettes.id')
    if (palettesFilter && palettesFilter.size > 0) {
      newSearchParams.set('palettes', Array.from(palettesFilter).join(','))
    } else {
      newSearchParams.delete('palettes')
    }

    // Handle search term
    if (currentSearchTerm) {
      newSearchParams.set('search', currentSearchTerm)
    } else {
      newSearchParams.delete('search')
    }

    // Update URL without causing navigation
    setSearchParams(newSearchParams, { replace: true })
  }

  // Initialize filters from URL on component mount
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const urlFilters = parseFiltersFromURL()
    setFilters(urlFilters)

    // Also sync search term from URL params if it changes
    const searchParam = searchParams.get('search')
    setSearchTerm(searchParam || '')
  }, [searchParams.toString()])

  // Update URL when filters or search term change (but not on initial load)
  useEffect(() => {
    if (isInitialized) {
      updateURLFromFilters(filters, searchTerm)
    } else {
      setIsInitialized(true)
    }
  }, [filters, searchTerm])

  // Example usage of impersonate function

  // Prepare filters and graphQL params to query data
  useEffect(() => {
    if (showDeleted) {
      // Load deleted customers (no filters for deleted view)
      loadDeletedCustomers({
        variables: {
          searchTerm,
          language: 'lt',
          take: pagination.pageSize,
          skip: pagination.pageIndex * pagination.pageSize,
          orderBy: getOrderSortParams(order),
        },
      })
    } else {
      // Load active customers with filters
      const queryFilter = Array.from(filters.entries())
        .map(([key, value]) => {
          if (!value.size) return null
          if (key === 'tags.id') {
            return {
              tags: {
                some: {
                  id: {
                    in: Array.from(value).map(id => Number(id)),
                  },
                },
              },
            }
          }
          if (key === 'fabric_palettes.id') {
            return {
              fabric_palettes: {
                some: {
                  id: {
                    in: Array.from(value).map(id => Number(id)),
                  },
                },
              },
            }
          }
          if (key === 'b2b_company_country') {
            return {
              b2b_company_address: {
                is: {
                  country: {
                    in: Array.from(value),
                  },
                },
              },
            }
          }
          // Convert string IDs to numbers for GraphQL
          if (key === 'customer_groupId' || key === 'price_listId') {
            return {
              [key]: {
                in: Array.from(value).map(id => Number(id)),
              },
            }
          }
          return {
            [key]: {
              in: Array.from(value),
            },
          }
        })
        .filter(Boolean)

      loadCustomers({
        variables: {
          searchTerm,
          language: 'lt',
          take: pagination.pageSize,
          skip: pagination.pageIndex * pagination.pageSize,
          filters: queryFilter,
          orderBy: getOrderSortParams(order),
        },
      })
    }
  }, [searchTerm, pagination, order, filters, showDeleted])

  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
  }, [searchTerm, pagination.pageSize, order, showDeleted])

  useEffect(() => {
    if (showDeleted && deletedData) {
      setTableData(deletedData.findManyCustomer)
      setTableDataCount(deletedData.findManyCustomerCount)
    } else if (!showDeleted && data) {
      setTableData(data.findManyCustomer)
      setTableDataCount(data.findManyCustomerCount)
    }
  }, [data, deletedData, showDeleted])

  const openAdvancedCustomerGroupForm = useOpenDrawer(CustomerGroupForm)
  const openB2BCustomerForm = useOpenDrawer(B2BCustomerForm)
  const openAdvancedCustomerForm = useOpenDrawer(B2BCustomerForm)

  // Handle restore customer
  const handleRestoreCustomer = (customerId: number) => {
    restoreCustomer({
      variables: {
        id: customerId,
        restoreAsB2B: true,
      },
    })
  }

  const columns = getColumns(
    openAdvancedCustomerGroupForm,
    openAdvancedCustomerForm,
    showDeleted ? handleRestoreCustomer : undefined
  )

  // handleImpersonate(row.id)
  const heading = showDeleted ? 'Deleted B2B Customers' : 'B2B Customers'
  const text = showDeleted
    ? 'View and restore deleted customers'
    : 'Manage your B2B customers here'

  return (
    <PageContent>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <HeaderSection title={heading} subtitle={text} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant={showDeleted ? 'default' : 'outline'}
            onClick={() => setShowDeleted(!showDeleted)}
          >
            {showDeleted ? 'View Active' : 'View Deleted'}
          </Button>
          {!showDeleted && (
            <Button onClick={openB2BCustomerForm}>Create New +</Button>
          )}
        </div>
      </div>
      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        isLoading={showDeleted ? deletedLoading : loading}
        isMultiSelect={!showDeleted}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              onBulkEdit: handleBulkEdit,
              onSelectAllFiltered: handleSelectAllFiltered,
              totalFilteredCount: tableDataCount,
              isAllFilteredSelected: isAllFilteredSelected,
              onClearSelection: handleClearSelection,
              search: {
                placeholder: 'Filter customers...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
                initialValue: searchTerm,
              },
              columns: [
                {
                  title: 'Customer Groups',
                  accessor: 'customer_groupId',
                  options: customersGroups?.map((group: any) => ({
                    label: group.name,
                    value: String(group.id),
                  })),
                },
                {
                  title: 'Price List',
                  accessor: 'price_listId',
                  options: priceLists?.map((group: any) => ({
                    label: group.name,
                    value: String(group.id),
                  })),
                },
                {
                  title: 'Tags',
                  accessor: 'tags.id',
                  options: tags?.map((tag: any) => ({
                    label: tag.tag_profiles[0]?.name || 'Unnamed Tag',
                    value: String(tag.id),
                  })),
                },
                {
                  title: 'Country',
                  accessor: 'b2b_company_country',
                  options: countryOptions,
                },
                {
                  title: 'Palettes',
                  accessor: 'fabric_palettes.id',
                  options: palettes?.map((palette: any) => ({
                    label: palette.name,
                    value: String(palette.id),
                  })),
                },
              ],
              initialFilters: filters,
            }}
          />
        )}
      />

      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        onSubmit={handleBulkEditSubmit}
        selectedCustomers={selectedCustomersForBulkEdit}
        isLoading={bulkUpdateLoading}
        selectionMode={selectionMode}
        totalFilteredCount={tableDataCount}
        editProperty={bulkEditProperty}
        availablePalettes={palettes || []}
      />
    </PageContent>
  )
}
